import { hasProp } from 'util/objects'
import { queryParams } from 'util/params'
import { getActorCurried } from 'util/actors'
import { removeHTMLTags } from 'util/strings'
import { decorate } from 'selectors/tickets/shared/util'

const getFirstExisting = (draft, ticket, key) => {
  if (hasProp(draft, key)) return draft[key]
  return ticket[key]
}

function ticketFieldsFromUrl() {
  const params = queryParams()
  const ticketFields = {}

  if (params.state) {
    // how should we handle unknown states?
    ticketFields.state = params.state.toLowerCase()
  }

  if (params.group) {
    ticketFields.group = params.group.toLowerCase()
  }

  if (params.agent) {
    ticketFields.agent = params.agent.toLowerCase()
  }

  return ticketFields
}

export const createReplyDraftBuilder = (
  drafts,
  ticket,
  agentsById,
  groupsById,
  currentUser,
  defaultNote,
  replyDraftPropsFromPrefs,
  firstMailboxId,
  previousMailboxId,
  preferredMailboxId,
  currentMailboxId,
  customersById,
  mailboxIdFromUrl,
  labelsById
) => {
  if (!ticket) return {}

  const getActor = getActorCurried(agentsById, customersById)
  const ticketDraft = drafts[ticket.id]

  // For the most part, we now have 90% of what we need to set up for drafts in
  // redux, which
  //
  //   - ensures changes to local saved draft (ticketDraft) are preserved
  //   - guards against legacy drafts from server that only have a body.
  //    Otherwise, we end up in state where draft doesn't include recipients.
  //    (the _PAGE reducers give us sane defaults)
  //   - some easily derived props/defaults: id, draft type, conversationType etc.
  //
  // In short, take the saved draft (if it exists) as the source of truth.
  //
  // For new drafts, we do most of the initialization in redux. However, we have
  // some logic that we havent (yet) moved to redux. i.e. replyDraftPropsFromPrefs
  // So in this selector, we decorate draft with that. This is especially
  // important for brand new drafts. (and its expected that somewhere
  // else we initialize/save the new draft with the result of this selector)
  // (e.g. ReplyForm componentDidMount)
  //
  // Another point to note is that we dont save ticket drafts/recipients by type,
  // so you lose any recipient changes as you flick between these draft types.
  // Body is preserved however.
  //
  const draft = { ...replyDraftPropsFromPrefs, ...ticketDraft }

  draft.body = draft.body || null
  draft.to = getFirstExisting(draft, {}, 'to') || []
  draft.cc = getFirstExisting(draft, {}, 'cc') || []
  draft.bcc = getFirstExisting(draft, {}, 'bcc') || []

  let draftTitle = draft.title
  if (!ticketDraft || ticketDraft.title === undefined) {
    draftTitle = ticket.title
  }
  // GR: At first glance, it seems we are being overly cautious in building the
  // draft object below. We could simply destructure the draft? Im not sure;
  // perhaps we want to control exactly what a 'Draft' object looks like coming
  // out of this selector (and potentially going into localStorage). I think the
  // less we build in selectors, the more confidence we'll have in the stored
  // draft?
  const decorated = decorate(
    agentsById,
    groupsById,
    currentUser,
    customersById,
    labelsById
  )({
    assignee: getFirstExisting(draft, ticket, 'assignee'),
    assigned_group_id: getFirstExisting(draft, ticket, 'assigned_group_id'),
    id: ticket.id,
    ticketId: ticket.id,
    ticket,
    mailboxId:
      mailboxIdFromUrl ||
      getFirstExisting(draft, ticket, 'mailboxId') ||
      currentMailboxId ||
      previousMailboxId ||
      preferredMailboxId ||
      firstMailboxId,
    to: Array.isArray(draft.to)
      ? draft.to
          .map(to => {
            if (to.email) {
              // If To recipient already has email, we assume it was already
              // populated with all the necessary data previously and we don't
              // need to do it again. This allows us to support changing
              // recipient's name and email in the New Convo pages without
              // those changes being overwritten on subsequent draft updates.
              return to
            }

            return getActor(to)
          })
          .filter(e => e)
      : [draft.to],
    cc: draft.cc.map(getActor).filter(e => e),
    bcc: draft.bcc.map(getActor).filter(e => e),
    body: draft.body,
    bodySizeBytes: draft.bodySizeBytes,
    draftBodyPlainText: draft && draft.body && removeHTMLTags(draft.body),
    title: draftTitle,
    isForwarding: draft.isForwarding,
    isNote: draft.isNote,
    state:
      ticketFieldsFromUrl().state || getFirstExisting(draft, ticket, 'state'),
    quotedText: draft.quotedText,
    isQuotingText: draft.isQuotingText,
    attachments: draft.attachments,
    templateId: draft.templateId,
    bodyAuthor: currentUser,
    conversationType: draft.conversationType,
    snoozeUntil: draft.snoozeUntil,
    type: draft.type, // our page reducers now give us this!
    automaticActions: draft.automaticActions,
  })

  return decorated
}

export const createNoteDraftBuilder = (
  noteDrafts,
  ticket,
  agentsById,
  groupsById,
  currentUser,
  defaultNote,
  firstMailboxId,
  previousMailboxId,
  preferredMailboxId,
  currentMailboxId,
  customersById,
  mailboxIdFromUrl,
  labelsById
) => {
  if (!ticket) return {}
  const noteDraft = noteDrafts[ticket.id]
  const draft = noteDraft || defaultNote
  draft.body = draft.body || null

  let toRecipients
  if (ticket.id === 'new') {
    const to = getFirstExisting(draft, {}, 'to')
    const first = Array.isArray(to) ? to[0] : to
    const getActor = getActorCurried(agentsById, customersById)
    toRecipients = [getActor(first)]
  }

  const decorated = decorate(
    agentsById,
    groupsById,
    currentUser,
    customersById,
    labelsById
  )({
    assignee: getFirstExisting(draft, ticket, 'assignee'),
    assigned_group_id: getFirstExisting(draft, ticket, 'assigned_group_id'),
    id: ticket.id,
    ticketId: ticket.id,
    body: draft.body,
    noteDraftBodyPlainText: draft && draft.body && removeHTMLTags(draft.body),
    title: draft.title,
    isNote: true,
    state: getFirstExisting(draft, ticket, 'state'),
    snoozedUntil: ticket.snoozedUntil,
    attachments: draft.attachments,
    mailboxId:
      mailboxIdFromUrl ||
      getFirstExisting(draft, ticket, 'mailboxId') ||
      currentMailboxId ||
      previousMailboxId ||
      preferredMailboxId ||
      firstMailboxId,
    bodyAuthor: currentUser,
    to: toRecipients,
    snoozeUntil: draft.snoozeUntil,
  })

  return decorated
}

export const getPlainBodyOrFrozen = (ticketId, isReply) => (drafts, frozen) => {
  if (ticketId === undefined) return null
  const draft = drafts[ticketId]
  if (draft === undefined) return null
  // Always use the frozen for the body current ticket,
  if (frozen && frozen.id === ticketId) {
    const value = isReply
      ? frozen.replyDraftBodyPlainText
      : frozen.noteDraftBodyPlainText
    return value || null
  }
  // otherwise use the reply/note draft body for that ticket.
  return removeHTMLTags(draft.body).trim() || null
}
